import React, { useEffect, useRef, useState } from 'react';
import './LandingPage.css';
import LandingNav from '../components/landing/LandingNav';
import LandingIntroBlockStack from '../components/landing/LandingIntroBlockSections';
import PlatformCompanyInfo from '../components/landing/PlatformCompanyInfo';
import { loadPublicLandingIntro, peekCachedPublicLandingIntro } from '../utils/landingPublicContent';
import type { LandingIntroSection } from '../types/landingIntro';

interface LandingIntroPageProps {
  onLoginClick: () => void;
}

/** 홈과 동일: [data-landing-reveal]에 is-revealed를 붙여 opacity 복구 */
function useIntroScrollReveal(enabled: boolean, sectionCount: number) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const root = rootRef.current;
    if (!root) return;

    const targets = root.querySelectorAll<HTMLElement>('[data-landing-reveal]');
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' },
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled, sectionCount]);

  return rootRef;
}

const LandingIntroPage: React.FC<LandingIntroPageProps> = ({ onLoginClick }) => {
  const [sections, setSections] = useState<LandingIntroSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useIntroScrollReveal(!loading && !error, sections.length);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const cached = peekCachedPublicLandingIntro();
        if (cached?.length) {
          setSections(cached);
          setLoading(false);
        }

        const fresh = await loadPublicLandingIntro();
        if (cancelled) return;
        setSections(fresh);
        setError(null);
        setLoading(false);
      } catch (err) {
        console.error('서비스 소개 로드 실패:', err);
        if (!cancelled) {
          if (!peekCachedPublicLandingIntro()?.length) {
            setError('서비스 소개를 불러올 수 없습니다.');
          }
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="landing-page landing-page--loading">
        <div className="landing-loading-spinner" aria-label="로딩 중" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page landing-page--error">
        <LandingNav onLoginClick={onLoginClick} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="landing-page landing-intro-page">
      <LandingNav onLoginClick={onLoginClick} />

      <main className="landing-intro-main">
        <header className="landing-intro-header">
          <h1 className="landing-intro-title">서비스 소개</h1>
          <p className="landing-intro-lead">Introduction to Class Planner</p>
        </header>

        {sections.length === 0 ? (
          <p className="landing-intro-empty">등록된 소개 섹션이 없습니다.</p>
        ) : (
          <LandingIntroBlockStack sections={sections} />
        )}
      </main>

      <PlatformCompanyInfo reveal={false} />
    </div>
  );
};

export default LandingIntroPage;
